import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Table,
  Row,
  Col,
  Statistic,
  DatePicker,
  Select,
  Space,
  Spin,
  message,
  Progress,
  Tag
} from 'antd';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  ShoppingCartOutlined,
  ShoppingOutlined,
  InboxOutlined,
  DollarOutlined,
  RiseOutlined,
  FallOutlined
} from '@ant-design/icons';
import { reportAPI } from '../../services/api';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { Option } = Select;

const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];

const ReportCenter = () => {
  const [activeTab, setActiveTab] = useState('sales');
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);

  // 销售报表数据
  const [salesData, setSalesData] = useState({
    summary: {},
    monthlyStats: [],
    customerStats: [],
    productStats: [],
    trendData: []
  });

  // 采购报表数据
  const [purchaseData, setPurchaseData] = useState({
    summary: {},
    monthlyStats: [],
    supplierStats: [],
    productStats: []
  });

  // 库存报表数据
  const [inventoryData, setInventoryData] = useState({
    summary: {},
    warehouseStats: [],
    alertStats: [],
    turnoverData: []
  });

  // 财务报表数据
  const [financeData, setFinanceData] = useState({
    summary: {},
    receivableAging: {},
    payableAging: {},
    cashflowTrend: []
  });

  useEffect(() => {
    loadReportData(activeTab);
  }, [activeTab, dateRange]);

  const loadReportData = async (tab) => {
    setLoading(true);
    const params = {
      startDate: dateRange[0]?.format('YYYY-MM-DD'),
      endDate: dateRange[1]?.format('YYYY-MM-DD')
    };

    try {
      switch (tab) {
        case 'sales':
          await loadSalesReport(params);
          break;
        case 'purchase':
          await loadPurchaseReport(params);
          break;
        case 'inventory':
          await loadInventoryReport(params);
          break;
        case 'finance':
          await loadFinanceReport(params);
          break;
        default:
          break;
      }
    } catch (error) {
      message.error('加载报表数据失败');
    } finally {
      setLoading(false);
    }
  };

  const loadSalesReport = async (params) => {
    const [summaryRes, customerRes, productRes, trendRes] = await Promise.all([
      reportAPI.getSalesSummary(params),
      reportAPI.getSalesByCustomer(params),
      reportAPI.getSalesByProduct(params),
      reportAPI.getSalesTrend({ ...params, groupBy: 'day' })
    ]);

    setSalesData({
      summary: summaryRes.data?.data?.summary || {},
      monthlyStats: summaryRes.data?.data?.monthlyStats || [],
      customerStats: customerRes.data?.data || [],
      productStats: productRes.data?.data || [],
      trendData: trendRes.data?.data || []
    });
  };

  const loadPurchaseReport = async (params) => {
    const [summaryRes, supplierRes, productRes] = await Promise.all([
      reportAPI.getPurchaseSummary(params),
      reportAPI.getPurchaseBySupplier(params),
      reportAPI.getPurchaseByProduct(params)
    ]);

    setPurchaseData({
      summary: summaryRes.data?.data?.summary || {},
      monthlyStats: summaryRes.data?.data?.monthlyStats || [],
      supplierStats: supplierRes.data?.data || [],
      productStats: productRes.data?.data || []
    });
  };

  const loadInventoryReport = async (params) => {
    const [summaryRes, turnoverRes] = await Promise.all([
      reportAPI.getInventorySummary(),
      reportAPI.getInventoryTurnover(params)
    ]);

    setInventoryData({
      summary: summaryRes.data?.data?.summary || {},
      warehouseStats: summaryRes.data?.data?.warehouseStats || [],
      alertStats: summaryRes.data?.data?.alertStats || [],
      turnoverData: turnoverRes.data?.data || []
    });
  };

  const loadFinanceReport = async (params) => {
    const [summaryRes, receivableAgingRes, payableAgingRes, cashflowRes] = await Promise.all([
      reportAPI.getFinanceSummary(params),
      reportAPI.getReceivableAging(),
      reportAPI.getPayableAging(),
      reportAPI.getCashflowTrend({ ...params, groupBy: 'day' })
    ]);

    setFinanceData({
      summary: summaryRes.data?.data || {},
      receivableAging: receivableAgingRes.data?.data?.summary || {},
      payableAging: payableAgingRes.data?.data?.summary || {},
      cashflowTrend: cashflowRes.data?.data || []
    });
  };

  // 销售报表内容
  const renderSalesReport = () => (
    <Spin spinning={loading}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="销售订单数"
              value={salesData.summary.totalOrders || 0}
              prefix={<ShoppingCartOutlined />}
              suffix="单"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="销售总额"
              value={salesData.summary.totalAmount || 0}
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="平均订单金额"
              value={salesData.summary.avgOrderAmount || 0}
              precision={2}
              prefix="¥"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="销售趋势">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={salesData.trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip formatter={(value) => `¥${Number(value).toFixed(2)}`} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="amount"
                  name="销售额"
                  stroke="#1890ff"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="客户销售排行 TOP10">
            <Table
              dataSource={salesData.customerStats}
              columns={[
                {
                  title: '客户名称',
                  dataIndex: ['customer', 'name'],
                  key: 'name'
                },
                {
                  title: '订单数',
                  dataIndex: 'orderCount',
                  key: 'orderCount',
                  align: 'center'
                },
                {
                  title: '销售额',
                  dataIndex: 'totalAmount',
                  key: 'totalAmount',
                  align: 'right',
                  render: (val) => `¥${Number(val).toFixed(2)}`
                }
              ]}
              pagination={false}
              size="small"
              rowKey={(record) => record.customerId}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="热销商品排行 TOP10">
            <Table
              dataSource={salesData.productStats}
              columns={[
                {
                  title: '商品编码',
                  dataIndex: ['product', 'code'],
                  key: 'code',
                  width: 120
                },
                {
                  title: '商品名称',
                  dataIndex: ['product', 'name'],
                  key: 'name'
                },
                {
                  title: '销售数量',
                  dataIndex: 'totalQuantity',
                  key: 'totalQuantity',
                  align: 'center',
                  render: (val, record) => `${val} ${record.product?.unit || ''}`
                },
                {
                  title: '销售金额',
                  dataIndex: 'totalAmount',
                  key: 'totalAmount',
                  align: 'right',
                  render: (val) => `¥${Number(val).toFixed(2)}`
                }
              ]}
              pagination={false}
              size="small"
              rowKey={(record) => record.productId}
            />
          </Card>
        </Col>
      </Row>
    </Spin>
  );

  // 采购报表内容
  const renderPurchaseReport = () => (
    <Spin spinning={loading}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="采购订单数"
              value={purchaseData.summary.totalOrders || 0}
              prefix={<ShoppingOutlined />}
              suffix="单"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="采购总额"
              value={purchaseData.summary.totalAmount || 0}
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="平均订单金额"
              value={purchaseData.summary.avgOrderAmount || 0}
              precision={2}
              prefix="¥"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="供应商采购排行 TOP10">
            <Table
              dataSource={purchaseData.supplierStats}
              columns={[
                {
                  title: '供应商名称',
                  dataIndex: ['supplier', 'name'],
                  key: 'name'
                },
                {
                  title: '订单数',
                  dataIndex: 'orderCount',
                  key: 'orderCount',
                  align: 'center'
                },
                {
                  title: '采购额',
                  dataIndex: 'totalAmount',
                  key: 'totalAmount',
                  align: 'right',
                  render: (val) => `¥${Number(val).toFixed(2)}`
                }
              ]}
              pagination={false}
              size="small"
              rowKey={(record) => record.supplierId}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="商品采购排行 TOP10">
            <Table
              dataSource={purchaseData.productStats}
              columns={[
                {
                  title: '商品名称',
                  dataIndex: ['product', 'name'],
                  key: 'name'
                },
                {
                  title: '采购数量',
                  dataIndex: 'totalQuantity',
                  key: 'totalQuantity',
                  align: 'center'
                },
                {
                  title: '采购金额',
                  dataIndex: 'totalAmount',
                  key: 'totalAmount',
                  align: 'right',
                  render: (val) => `¥${Number(val).toFixed(2)}`
                }
              ]}
              pagination={false}
              size="small"
              rowKey={(record) => record.productId}
            />
          </Card>
        </Col>
      </Row>
    </Spin>
  );

  // 库存报表内容
  const renderInventoryReport = () => {
    const alertData = inventoryData.alertStats.map(item => ({
      name: item.alertType === 'low' ? '库存不足' : item.alertType === 'high' ? '库存过高' : '正常',
      value: parseInt(item.count) || 0
    }));

    return (
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="库存商品种类"
                value={inventoryData.summary.totalRecords || 0}
                prefix={<InboxOutlined />}
                suffix="种"
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="库存总数量"
                value={inventoryData.summary.totalQuantity || 0}
                precision={0}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="库存总价值"
                value={inventoryData.summary.totalValue || 0}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <Card title="仓库库存分布">
              <Table
                dataSource={inventoryData.warehouseStats}
                columns={[
                  {
                    title: '仓库',
                    dataIndex: ['warehouse', 'name'],
                    key: 'name'
                  },
                  {
                    title: '商品种类',
                    dataIndex: 'productCount',
                    key: 'productCount',
                    align: 'center'
                  },
                  {
                    title: '库存数量',
                    dataIndex: 'totalQuantity',
                    key: 'totalQuantity',
                    align: 'center'
                  },
                  {
                    title: '库存价值',
                    dataIndex: 'totalValue',
                    key: 'totalValue',
                    align: 'right',
                    render: (val) => `¥${Number(val).toFixed(2)}`
                  }
                ]}
                pagination={false}
                size="small"
                rowKey={(record) => record.warehouseId}
              />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="库存预警状态">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={alertData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {alertData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Card title="库存周转率">
              <Table
                dataSource={inventoryData.turnoverData}
                columns={[
                  {
                    title: '商品编码',
                    dataIndex: ['product', 'code'],
                    key: 'code',
                    width: 120
                  },
                  {
                    title: '商品名称',
                    dataIndex: ['product', 'name'],
                    key: 'name'
                  },
                  {
                    title: '出库数量',
                    dataIndex: 'outQuantity',
                    key: 'outQuantity',
                    align: 'center'
                  },
                  {
                    title: '当前库存',
                    dataIndex: 'currentStock',
                    key: 'currentStock',
                    align: 'center'
                  },
                  {
                    title: '周转率',
                    dataIndex: 'turnoverRate',
                    key: 'turnoverRate',
                    align: 'center',
                    render: (val) => {
                      const rate = parseFloat(val);
                      let color = rate > 1 ? 'green' : rate > 0.5 ? 'orange' : 'red';
                      return <Tag color={color}>{rate}</Tag>;
                    }
                  }
                ]}
                pagination={{ pageSize: 10 }}
                size="small"
                rowKey={(record) => record.product?.id}
              />
            </Card>
          </Col>
        </Row>
      </Spin>
    );
  };

  // 财务报表内容
  const renderFinanceReport = () => {
    const agingLabels = ['未到期', '1-30天', '31-60天', '61-90天', '90天以上'];
    const receivableAgingData = [
      { name: '未到期', amount: financeData.receivableAging.current?.amount || 0 },
      { name: '1-30天', amount: financeData.receivableAging.days30?.amount || 0 },
      { name: '31-60天', amount: financeData.receivableAging.days60?.amount || 0 },
      { name: '61-90天', amount: financeData.receivableAging.days90?.amount || 0 },
      { name: '90天以上', amount: financeData.receivableAging.over90?.amount || 0 }
    ];
    const payableAgingData = [
      { name: '未到期', amount: financeData.payableAging.current?.amount || 0 },
      { name: '1-30天', amount: financeData.payableAging.days30?.amount || 0 },
      { name: '31-60天', amount: financeData.payableAging.days60?.amount || 0 },
      { name: '61-90天', amount: financeData.payableAging.days90?.amount || 0 },
      { name: '90天以上', amount: financeData.payableAging.over90?.amount || 0 }
    ];

    return (
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="应收总额"
                value={financeData.summary.receivable?.totalAmount || 0}
                precision={2}
                prefix="¥"
              />
              <div style={{ marginTop: 8 }}>
                <span style={{ color: '#52c41a' }}>已收: ¥{(financeData.summary.receivable?.receivedAmount || 0).toFixed(2)}</span>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="待收金额"
                value={financeData.summary.receivable?.pendingAmount || 0}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="应付总额"
                value={financeData.summary.payable?.totalAmount || 0}
                precision={2}
                prefix="¥"
              />
              <div style={{ marginTop: 8 }}>
                <span style={{ color: '#52c41a' }}>已付: ¥{(financeData.summary.payable?.paidAmount || 0).toFixed(2)}</span>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="待付金额"
                value={financeData.summary.payable?.pendingAmount || 0}
                precision={2}
                prefix="¥"
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <Card title="应收账龄分析">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={receivableAgingData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => `¥${Number(value).toFixed(2)}`} />
                  <Bar dataKey="amount" name="金额" fill="#1890ff" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="应付账龄分析">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={payableAgingData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => `¥${Number(value).toFixed(2)}`} />
                  <Bar dataKey="amount" name="金额" fill="#f5222d" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Card title="收支趋势">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={financeData.cashflowTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip formatter={(value) => `¥${Number(value).toFixed(2)}`} />
                  <Legend />
                  <Line type="monotone" dataKey="receipt" name="收款" stroke="#52c41a" strokeWidth={2} />
                  <Line type="monotone" dataKey="payment" name="付款" stroke="#f5222d" strokeWidth={2} />
                  <Line type="monotone" dataKey="net" name="净额" stroke="#1890ff" strokeWidth={2} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      </Spin>
    );
  };

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space>
          <span>统计周期:</span>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            allowClear={false}
          />
          <Select
            style={{ width: 120 }}
            defaultValue="month"
            onChange={(value) => {
              const today = dayjs();
              switch (value) {
                case 'week':
                  setDateRange([today.startOf('week'), today.endOf('week')]);
                  break;
                case 'month':
                  setDateRange([today.startOf('month'), today.endOf('month')]);
                  break;
                case 'quarter':
                  setDateRange([today.startOf('quarter'), today.endOf('quarter')]);
                  break;
                case 'year':
                  setDateRange([today.startOf('year'), today.endOf('year')]);
                  break;
                default:
                  break;
              }
            }}
          >
            <Option value="week">本周</Option>
            <Option value="month">本月</Option>
            <Option value="quarter">本季度</Option>
            <Option value="year">本年</Option>
          </Select>
        </Space>
      </Card>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane
            tab={<span><ShoppingCartOutlined />销售报表</span>}
            key="sales"
          >
            {renderSalesReport()}
          </TabPane>
          <TabPane
            tab={<span><ShoppingOutlined />采购报表</span>}
            key="purchase"
          >
            {renderPurchaseReport()}
          </TabPane>
          <TabPane
            tab={<span><InboxOutlined />库存报表</span>}
            key="inventory"
          >
            {renderInventoryReport()}
          </TabPane>
          <TabPane
            tab={<span><DollarOutlined />财务报表</span>}
            key="finance"
          >
            {renderFinanceReport()}
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default ReportCenter;
